import { useState } from 'react';
import { shareLinkFor } from '../url';
import { QrCode } from './QrCode';

async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
    // Fallback for browsers/contexts without the async clipboard API.
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    document.execCommand('copy');
    document.body.removeChild(ta);
    return true;
  } catch {
    // Clipboard write can throw (permissions); the code/link is still
    // visible as selectable text, so the user can copy manually.
    return false;
  }
}

/** True iff `navigator.share` is present -- exported (not just used
 * internally) so unit tests can exercise both branches by stubbing
 * `navigator.share` directly, without needing a real mobile share sheet.
 * `navigator.canShare` is checked too when present, but its ABSENCE is not
 * treated as "unsupported" (some real browsers implement `share` without
 * `canShare` -- MDN). */
export function canUseWebShare(): boolean {
  if (typeof navigator === 'undefined' || typeof navigator.share !== 'function') return false;
  if (typeof navigator.canShare === 'function') {
    try {
      return navigator.canShare({ title: 't', text: 't', url: 'https://example.com' });
    } catch {
      return true;
    }
  }
  return true;
}

export interface ShareRoomProps {
  code: string;
}

/** Reusable room-sharing UI: team code, a Share button that uses the Web
 * Share API (mobile share sheet) when available, falling back to Copy
 * link / Copy code. Used in the Lobby and in the in-game pause menu (so a
 * seat can be filled mid-game) -- one component, one behavior, everywhere
 * sharing is offered. */
export function ShareRoom({ code }: ShareRoomProps): JSX.Element {
  const [status, setStatus] = useState<'code' | 'link' | 'shared' | null>(null);
  const hasWebShare = canUseWebShare();
  const link = shareLinkFor(code);

  function flash(which: 'code' | 'link' | 'shared'): void {
    setStatus(which);
    setTimeout(() => setStatus((s) => (s === which ? null : s)), 1500);
  }

  async function doCopy(text: string, which: 'code' | 'link'): Promise<void> {
    if (await copyText(text)) flash(which);
  }

  async function doShare(): Promise<void> {
    if (hasWebShare) {
      try {
        await navigator.share({
          title: 'PACMATES',
          text: `Join my PacMates game, team code ${code}`,
          url: link,
        });
        flash('shared');
        return;
      } catch {
        // User cancelled the share sheet, or it genuinely failed -- fall
        // through to a plain link copy so the action still does SOMETHING
        // useful rather than silently no-op-ing.
      }
    }
    await doCopy(link, 'link');
  }

  return (
    <div className="copy-code" data-testid="share-room">
      <div className="copy-code-value" aria-label="Team code">
        {code}
      </div>
      {/* PART 5: QR "scan to join" -- the most expected mobile invite
          affordance. Renders nothing at all (see QrCode.tsx) if the share
          link somehow exceeds this app's fixed QR capacity, so an unusually
          long deployment URL degrades to "no QR" rather than a broken one
          or a stray empty chip; the code/copy/share affordances below are
          completely unaffected either way. */}
      <QrCode value={link} label={`QR code to join room ${code}`} />
      <p className="dim share-room-hint">Send this to a friend to play together.</p>
      <div className="copy-code-actions">
        {/* PER-SCREEN 03/03b fix (ROUND 3, replaces DEFECT 8's fix): this
            button used to flip `btn-primary` (yellow) whenever a seat was
            still open, on the theory that inviting a partner IS the goal
            action then -- but that put TWO competing filled-yellow
            primaries on screen 03 (this button AND Lobby's "I'm ready"
            toggle) and made the button's own prominence inconsistent
            across the two lobby states (yellow when open, outline once
            both seats fill). "I'm ready" is the single real goal action
            of the lobby in either state; Copy link/Share is always a
            secondary utility -- same outline treatment as "Copy code"
            right next to it, in every caller (Lobby AND PauseOverlay). */}
        <button type="button" className="btn btn-secondary" onClick={doShare} data-testid="share-button">
          {status === 'shared' ? 'Shared!' : hasWebShare ? 'Share' : status === 'link' ? 'Copied!' : 'Copy link'}
        </button>
        <button type="button" className="btn btn-secondary" onClick={() => doCopy(code, 'code')}>
          {status === 'code' ? 'Copied!' : 'Copy code'}
        </button>
        {hasWebShare && (
          <button type="button" className="btn btn-secondary" onClick={() => doCopy(link, 'link')}>
            {status === 'link' ? 'Copied!' : 'Copy link'}
          </button>
        )}
      </div>
    </div>
  );
}
